import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertCircle, RotateCcw, Clock, Zap, ListChecks, FileQuestion } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { useAttemptStore } from "@/store/attemptStore";
import type { ReplayAttempt, ReplayDecision, ReplayResponse } from "@/types/simulation";

function bandToken(band: string | null): { color: string; bg: string; label: string } {
  const v = (band ?? "").toUpperCase();
  if (v.includes("HIGH") || v.includes("STRONG")) return { color: "#10B981", bg: "rgba(16,185,129,0.12)", label: band ?? "Strong" };
  if (v.includes("MEDIUM") || v.includes("DEVELOP")) return { color: "#F59E0B", bg: "rgba(245,158,11,0.12)", label: band ?? "Developing" };
  if (v.includes("LOW") || v.includes("NEED")) return { color: "#F87272", bg: "rgba(248,114,114,0.12)", label: band ?? "Needs work" };
  return { color: "#A78BFA", bg: "rgba(167,139,250,0.10)", label: band ?? "Complete" };
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m === 0 ? `${s}s` : `${m}m ${String(s).padStart(2, "0")}s`;
}

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * ReplayPage — side-by-side comparison of a learner's own past attempts on a
 * simulation. Decisions are aligned by sequence_no — when branching means
 * different attempts hit different variant nodes, the comparison shows them
 * side-by-side as well (visualising "what did the high path vs the low path
 * actually look like for the same slot").
 */
export function ReplayPage(): JSX.Element {
  const { simId } = useParams<{ simId: string }>();
  const navigate = useNavigate();
  const { fetchReplay } = useAttemptStore();

  const [data, setData] = useState<ReplayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const load = async (): Promise<void> => {
    if (simId === undefined) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReplay(simId);
      setData(res);
      // Default selection: 2 most recent attempts (last 2 by attempt_no order)
      const recent = res.attempts.slice(-2).map((a) => a.id);
      setSelectedIds(recent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load your replay");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simId]);

  const selectedAttempts = useMemo(() => {
    if (data === null) return [];
    const ids = new Set(selectedIds);
    return data.attempts.filter((a) => ids.has(a.id));
  }, [data, selectedIds]);

  // Union of all sequence_nos across selected attempts, sorted.
  const allSequences = useMemo(() => {
    const set = new Set<number>();
    for (const a of selectedAttempts) for (const d of a.decisions) set.add(d.sequenceNo);
    return [...set].sort((x, y) => x - y);
  }, [selectedAttempts]);

  const toggleAttempt = (id: string): void => {
    setSelectedIds((cur) => {
      if (cur.includes(id)) {
        if (cur.length <= 1) return cur; // keep at least 1 selected
        return cur.filter((x) => x !== id);
      }
      if (cur.length >= 3) {
        // swap: drop oldest, add new (keep at most 3 in view)
        return [...cur.slice(1), id];
      }
      return [...cur, id];
    });
  };

  return (
    <ScreenShell>
      <SimHeader decision={data?.attempts.length ?? 0} xp={data?.attempts.reduce((m, a) => Math.max(m, a.totalXp), 0) ?? 0} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <button
          type="button"
          onClick={() => navigate("/simulations")}
          className="mb-5 inline-flex items-center gap-1.5 text-xs text-white/50 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to simulations
        </button>

        {loading && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
              className="h-12 w-12 rounded-full border-2 border-white/10 border-t-fuchsia-400" />
            <p className="mt-5 text-sm text-white/60">Pulling your past attempts…</p>
          </div>
        )}

        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-10 max-w-lg p-10 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-[#f87272]" />
            <h2 className="mt-4 text-lg font-semibold">We couldn&rsquo;t load your replay</h2>
            <p className="mt-1 text-sm text-white/60">{error}</p>
            <div className="mt-6 flex justify-center">
              <button type="button" onClick={() => void load()}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2 text-sm font-medium text-white hover:border-white/30 hover:bg-white/10">
                <RotateCcw className="h-4 w-4" /> Try again
              </button>
            </div>
          </GlassCard>
        )}

        {!loading && error === null && data !== null && (
          <>
            {/* Heading */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300">🔁 Replay</div>
              <h1 className="mt-2 text-[28px] font-bold leading-tight">
                <GradientText>{data.simulationTitle}</GradientText>
              </h1>
              {data.competencyName !== null && (
                <p className="mt-1 text-sm text-white/55">{data.competencyName} · {data.attempts.length} past attempts</p>
              )}
            </motion.div>

            {data.attempts.length < 2 ? (
              <GlassCard className="mt-8 p-8 text-center">
                <FileQuestion className="mx-auto h-10 w-10 text-white/30" />
                <h2 className="mt-3 text-base font-semibold">You need at least two completed attempts to use Replay.</h2>
                <p className="mt-1 text-sm text-white/55">
                  Complete one more attempt and come back to compare them side-by-side.
                </p>
              </GlassCard>
            ) : (
              <>
                {/* Attempt selector pills */}
                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">Compare</span>
                  {data.attempts.map((a) => {
                    const selected = selectedIds.includes(a.id);
                    const t = bandToken(a.finalBand);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleAttempt(a.id)}
                        className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors ${
                          selected
                            ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-white"
                            : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/25 hover:text-white"
                        }`}
                      >
                        #{a.attemptNo} · {a.totalScore}/{a.maxPossible}
                        {a.finalBand !== null && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: t.color }} />
                            <span className="text-[10px] uppercase tracking-[0.1em]" style={{ color: t.color }}>
                              {a.status === "timed_out" ? "Timed out" : t.label}
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                  <span className="ml-auto text-[10px] text-white/35">Choose 1–3 attempts</span>
                </div>

                {/* Attempt header cards */}
                <div
                  className="mt-5 grid gap-4"
                  style={{ gridTemplateColumns: `repeat(${Math.max(1, selectedAttempts.length)}, minmax(0, 1fr))` }}
                >
                  {selectedAttempts.map((a) => {
                    const t = bandToken(a.finalBand);
                    return (
                      <GlassCard key={a.id} className="p-5" animatedBorder>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
                            Attempt #{a.attemptNo}
                          </span>
                          {a.finalBand !== null && (
                            <span
                              className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]"
                              style={{ background: t.bg, border: `1px solid ${t.color}80`, color: t.color }}
                            >
                              {a.status === "timed_out" ? "Timed out" : t.label}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex items-end gap-1.5">
                          <span className="font-mono text-3xl font-bold text-white">{a.totalScore}</span>
                          <span className="mb-1 text-xs text-white/40">/ {a.maxPossible}</span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/50">
                          <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-cyan-300" /> {a.totalXp} XP</span>
                          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-fuchsia-300" /> {formatDuration(a.durationSeconds)}</span>
                          <span className="inline-flex items-center gap-1"><ListChecks className="h-3 w-3 text-violet-300" /> {a.decisions.length} decisions</span>
                        </div>
                        <p className="mt-2 text-[10px] text-white/35">{formatDate(a.endedAt)}</p>
                      </GlassCard>
                    );
                  })}
                </div>

                {/* Decision-by-decision rows */}
                <div className="mt-6">
                  <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-fuchsia-200">Decision-by-decision</h2>
                  <div className="mt-3 space-y-2">
                    {allSequences.map((seq) => (
                      <DecisionRow
                        key={seq}
                        sequenceNo={seq}
                        attempts={selectedAttempts}
                      />
                    ))}
                  </div>
                </div>

                <p className="mt-6 text-center text-[11px] text-white/35">
                  Rows are aligned by decision number. Where attempts hit different branches, the variant scenarios sit side-by-side so you can see where the path diverged.
                </p>
              </>
            )}
          </>
        )}
      </main>
    </ScreenShell>
  );
}

function DecisionRow({
  sequenceNo,
  attempts,
}: {
  sequenceNo: number;
  attempts: ReplayAttempt[];
}): JSX.Element {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `auto repeat(${Math.max(1, attempts.length)}, minmax(0, 1fr))` }}>
      <div className="flex h-full items-start justify-center pt-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[11px] font-bold text-white/60">
          {sequenceNo}
        </span>
      </div>
      {attempts.map((a) => {
        const d = a.decisions.find((x) => x.sequenceNo === sequenceNo) ?? null;
        return <DecisionCell key={a.id} decision={d} />;
      })}
    </div>
  );
}

function DecisionCell({ decision }: { decision: ReplayDecision | null }): JSX.Element {
  if (decision === null) {
    return (
      <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3">
        <p className="text-[11px] text-white/30">— Not reached on this attempt —</p>
      </div>
    );
  }
  const t = bandToken(decision.band);
  const answerLine =
    decision.questionType === "MultipleChoice"
      ? decision.choiceText
      : decision.answerText;
  const answerTrimmed = answerLine !== null && answerLine.length > 240 ? `${answerLine.slice(0, 240)}…` : answerLine;
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-medium leading-tight text-white/75">{decision.title ?? `Decision ${decision.sequenceNo}`}</p>
        <span className="shrink-0 font-mono text-sm font-bold" style={{ color: t.color }}>
          {decision.score}<span className="text-[10px] font-normal text-white/30">/10</span>
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-[0.1em] text-white/35">
        {decision.questionType === "MultipleChoice" ? "Multiple choice" : "Open text"} · {decision.nodeCode}
      </p>
      {answerTrimmed !== null && (
        <p className="text-[11px] leading-snug text-white/65">
          <span className="text-white/40">{decision.questionType === "MultipleChoice" ? "Picked: " : "You wrote: "}</span>
          {answerTrimmed}
        </p>
      )}
      {decision.band !== null && (
        <span
          className="self-start rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em]"
          style={{ background: t.bg, border: `1px solid ${t.color}55`, color: t.color }}
        >
          {decision.band}
        </span>
      )}
    </div>
  );
}
